import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import {
  CUSTOM_BRAND_MESSAGING_KEY,
  findEntitlement,
  isFeatureUsable,
  messagingMethodForLocationType,
} from '../../utils/productLicensing';
import {
  fetchMessageNotes,
  loadMessagingEntitlements,
  previewMessageTemplate,
} from './api';

const NOTE_LIMIT = 120;
const stripUrls = (value) =>
  String(value || '')
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/\bwww\.\S+/gi, '')
    .trim();

const CustomNotePicker = ({
  t,
  boxId,
  locationTypeId,
  value,
  onChange,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [licensed, setLicensed] = useState(false);
  const [typed, setTyped] = useState('');
  const [segmentError, setSegmentError] = useState('');
  const method = messagingMethodForLocationType(locationTypeId);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!boxId || !method) {
        setLicensed(false);
        return;
      }
      try {
        const data = await loadMessagingEntitlements();
        const entitlements = (data && data.entitlements) || [];
        const messaging = findEntitlement(entitlements, CUSTOM_BRAND_MESSAGING_KEY);
        const methodKey = method === 'kiosk' ? null : method;
        const methodOk = methodKey
          ? isFeatureUsable(findEntitlement(entitlements, methodKey))
          : true;
        const allowed = isFeatureUsable(messaging) && methodOk;
        if (cancelled) return;
        setLicensed(allowed);
        if (!allowed) return;
        const result = await fetchMessageNotes(boxId, method);
        if (cancelled) return;
        const list = (result && result.notes) || [];
        setNotes(list);
        if (!value || (!value.source && !value.text)) {
          const def = list.find((note) => note.is_default && note.is_enabled);
          if (def && onChange) {
            onChange({
              id: def.id,
              text: def.body || '',
              source: 'default',
            });
          }
        }
      } catch (_err) {
        if (!cancelled) setLicensed(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [boxId, method]);

  const enabledNotes = useMemo(
    () => (notes || []).filter((note) => note.is_enabled),
    [notes]
  );
  const displayText =
    value && value.source === 'cleared'
      ? ''
      : (value && value.text) || '';
  const label =
    value && value.source === 'cleared'
      ? t('MessageControl.NoCustomNote', { defaultValue: 'No custom note' })
      : displayText ||
        t('MessageControl.NoCustomNote', { defaultValue: 'No custom note' });

  const apply = async (next) => {
    setSegmentError('');
    if (next.source === 'typed' && next.text) {
      try {
        const preview = await previewMessageTemplate({
          box_id: boxId,
          method,
          channel: 'sms',
          custom_note: next.text,
          tokens: {
            firstName: 'Maria',
            accountName: 'Pharmacy',
            locationAddress: '123 Main St',
            pickupCode: 'ABC123',
            hours: '8am - 5pm',
            link: 'ilocal.link/xxxx',
          },
        });
        if (preview && preview.blocked) {
          setSegmentError(
            preview.blockReason ||
              t('MessageControl.SegmentBlocked', {
                defaultValue: 'Shorten the editable copy.',
              })
          );
          return;
        }
      } catch (_err) {
        // preview is advisory; still allow if the template is unpublished
      }
    }
    if (onChange) onChange(next);
    setOpen(false);
  };

  if (!licensed || disabled) {
    return null;
  }

  return (
    <div className="custom-note-picker">
      <button
        type="button"
        className="custom-note-picker-trigger"
        onClick={() => {
          setTyped(value && value.source === 'typed' ? value.text || '' : '');
          setSegmentError('');
          setOpen(true);
        }}
      >
        <span className="custom-note-picker-label">
          {t('MessageControl.CustomNote', { defaultValue: 'Custom note' })}
        </span>
        <span className="custom-note-picker-value">{label}</span>
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('MessageControl.CustomNote', { defaultValue: 'Custom note' })}
        </DialogTitle>
        <DialogContent>
          <p className="custom-note-picker-help">
            {t('MessageControl.PhiHelper', {
              defaultValue:
                'Do not include patient health information in this note.',
            })}
          </p>
          {enabledNotes.map((note) => (
            <Button
              key={note.id}
              fullWidth
              variant={
                value && value.id === note.id ? 'contained' : 'outlined'
              }
              sx={{ mt: 1, textTransform: 'none', justifyContent: 'flex-start' }}
              onClick={() =>
                apply({
                  id: note.id,
                  text: note.body || '',
                  source: note.is_default ? 'default' : 'alternate',
                })
              }
            >
              <span>
                <strong>{note.label || (note.is_default ? 'Default' : 'Alternate')}</strong>
                <br />
                {note.body}
              </span>
            </Button>
          ))}
          <TextField
            fullWidth
            multiline
            minRows={3}
            margin="normal"
            label={t('MessageControl.TypeOwn', { defaultValue: 'Type my own' })}
            value={typed}
            inputProps={{ maxLength: NOTE_LIMIT }}
            helperText={`${stripUrls(typed).length} / ${NOTE_LIMIT}`}
            onChange={(event) => setTyped(stripUrls(event.target.value))}
          />
          {segmentError ? (
            <p className="custom-note-picker-error">{segmentError}</p>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => apply({ id: null, text: '', source: 'cleared' })}>
            {t('MessageControl.Clear', { defaultValue: 'Clear' })}
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              apply({
                id: null,
                text: stripUrls(typed).slice(0, NOTE_LIMIT),
                source: 'typed',
              })
            }
          >
            {t('MessageControl.UseTyped', { defaultValue: 'Use this note' })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CustomNotePicker;



// WEBPACK FOOTER //
// ./src/components/message-control-center/CustomNotePicker.js